import { useCallback, useEffect, useState } from 'react';
import type { ComposerCapabilityCategory, ComposerCapabilityExample } from '@/types/composer';

interface UseCapabilityCategoryDrawerOptions {
  categories: ComposerCapabilityCategory[];
  onExampleHover?: (example: ComposerCapabilityExample | null) => void;
  onExampleSelect: (example: ComposerCapabilityExample) => void;
  /** Called when a category drawer opens or closes. */
  onOpenChange?: (open: boolean) => void;
  initialOpenCategoryId?: string | null;
  /** When false, outside-click / focus / Escape dismiss is not attached (e.g. mobile sheet). */
  dismissEnabled?: boolean;
  /** Nodes that should not trigger dismiss when clicked or focused. */
  getDismissIgnoreNodes?: () => (HTMLElement | null | undefined)[];
  /** Runs after select + close (e.g. close the + popover). */
  onAfterExampleSelect?: () => void;
}

export function useCapabilityCategoryDrawer({
  categories,
  onExampleHover,
  onExampleSelect,
  onOpenChange,
  initialOpenCategoryId = null,
  dismissEnabled = true,
  getDismissIgnoreNodes,
  onAfterExampleSelect,
}: UseCapabilityCategoryDrawerOptions) {
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(initialOpenCategoryId);

  const openCategory =
    categories.find((category) => category.id === openCategoryId) ?? null;

  const handleClose = useCallback(() => {
    setOpenCategoryId(null);
    onExampleHover?.(null);
  }, [onExampleHover]);

  const openCategoryById = useCallback(
    (categoryId: string) => {
      onExampleHover?.(null);
      setOpenCategoryId(categoryId);
    },
    [onExampleHover]
  );

  const toggleCategory = useCallback(
    (categoryId: string, canOpen: boolean) => {
      if (openCategoryId === categoryId) {
        handleClose();
      } else if (canOpen) {
        openCategoryById(categoryId);
      }
    },
    [handleClose, openCategoryById, openCategoryId]
  );

  const handleExampleSelect = useCallback(
    (example: ComposerCapabilityExample) => {
      onExampleSelect(example);
      handleClose();
      onAfterExampleSelect?.();
    },
    [handleClose, onAfterExampleSelect, onExampleSelect]
  );

  useEffect(() => {
    onOpenChange?.(Boolean(openCategoryId));
  }, [onOpenChange, openCategoryId]);

  useEffect(() => {
    if (openCategoryId && !openCategory) {
      handleClose();
    }
  }, [handleClose, openCategory, openCategoryId]);

  useEffect(() => {
    if (!openCategoryId || !dismissEnabled) return;

    const isIgnoredTarget = (target: Node) => {
      const nodes = getDismissIgnoreNodes?.() ?? [];
      return nodes.some((node) => node?.contains(target));
    };

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (isIgnoredTarget(target)) return;
      handleClose();
    };

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (isIgnoredTarget(target)) return;
      handleClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dismissEnabled, getDismissIgnoreNodes, handleClose, openCategoryId]);

  return {
    openCategoryId,
    openCategory,
    setOpenCategoryId,
    handleClose,
    openCategoryById,
    toggleCategory,
    handleExampleSelect,
  };
}
